import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { GIPointSetType, GiPointType } from '@/stores/type/giPoint.type'
import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import { useBaseStore } from '@haskoning/vue-basestore'
import type { CalculationCPTType } from '@/stores/type/calculationCPT.type'

import { useCalculationCPTStore } from '@/stores/calculationCPT'
import { useGiDataUploadCollectionStore } from '@/stores/giDataUploadCollection'

const api = useApi<GiPointType>()

const baseUrl = API.GI_DATA.GI_POINTS.INDEX
export const useGiPointStore = defineStore('giPoints', () => {
	const baseStore = useBaseStore<GiPointType>({ service: baseUrl, api })
	// State
	const filterFields = ref<string[]>(['name'])

	// Actions
	const resetAll = () => {
		baseStore.items.value = []
		baseStore.currentItem.value = null
		baseStore.fetchLoading.value = false
		baseStore.selectedItems.value = []
	}

	const fetchItems = async (filter: { [key: string]: string | number } = {}) => {
		baseStore.fetchLoading.value = true
		try {
			const response = (await api.service(baseUrl).find({ query: filter })) as unknown as
				| GIPointSetType
				| undefined
			baseStore.items.value = response.data.features
		} catch (e) {
			// Replace with your snackbar or notification logic
			console.error('Fetch items failed.', e)
			baseStore.fetchLoading.value = false
			return false
		}
		baseStore.fetchLoading.value = false
		return baseStore.items.value
	}

	const removeItem = (payload: GiPointType) => {
		const index = baseStore.items.value.findIndex((item) => payload.id === item.id)
		if (index !== -1) baseStore.items.value.splice(index, 1)
		if (baseStore.currentItem.value && payload.id === baseStore.currentItem.value.id) {
			baseStore.currentItem.value = null
		}
	}

	const pushSelectedItem = (item: GiPointType) => {
		if (item?.id) {
			item['gidatauploadcollection'] = item.properties.gi_data_upload_collection
			baseStore.selectedItems.value.push(item)
		}
	}

	const replaceAndPushItemsByCollectionId = (newItems: GiPointType[], id: number) => {
		baseStore.items.value = baseStore.items.value.filter(
			(item) => item.properties.gi_data_upload_collection !== id
		)
		baseStore.items.value.push(...newItems)
	}

	const toggleSelectedItem = (id: number) => {
		const index = baseStore.selectedItems.value.findIndex((selectedItem) => selectedItem.id === id)
		const giDataUploadCollectionStore = useGiDataUploadCollectionStore()
		const giPoint = baseStore.items.value.find((item) => item.id === id)
		if (giPoint) {
			const gi_data_upload_collection = giPoint.properties.gi_data_upload_collection
			if (index !== -1) {
				baseStore.selectedItems.value.splice(index, 1)
				giDataUploadCollectionStore.removeSelectedItem(gi_data_upload_collection)
			} else {
				pushSelectedItem(giPoint)
				const selectedItemLength = getSelectedItemsByCollectionId(gi_data_upload_collection).length
				const itemLength = getItemsByCollectionId(gi_data_upload_collection).length
				if (selectedItemLength === itemLength) {
					giDataUploadCollectionStore.addSelectedItem(giPoint?.properties.gi_data_upload_collection)
				}
			}
		}
	}

	const addSelectedItem = (id: number) => {
		const item = baseStore.items.value.find((item) => item.id === id)
		if (item) {
			const index = baseStore.selectedItems.value.findIndex(
				(selectedItem) => selectedItem.id === id
			)
			if (index === -1) {
				pushSelectedItem(item)
			}
		}
	}

	const removeSelectedItem = (id: number) => {
		const index = baseStore.selectedItems.value.findIndex((selectedItem) => selectedItem.id === id)
		if (index !== -1) {
			baseStore.selectedItems.value.splice(index, 1)
		}
	}

	const selectAllItemsByCollectionId = (id: number) => {
		const filtered = baseStore.items.value.filter(
			(item) => item.properties.gi_data_upload_collection === id
		)
		filtered.forEach((item) => {
			const index = baseStore.selectedItems.value.findIndex(
				(selectedItem) => selectedItem.id === item.id
			)
			if (index === -1) {
				pushSelectedItem(item)
			}
		})
	}

	const clearSelectedItemsByCollectionId = (id: number) => {
		const filtered = baseStore.items.value.filter(
			(item) => item.properties.gi_data_upload_collection === id
		)
		filtered.forEach((item) => {
			const index = baseStore.selectedItems.value.findIndex(
				(selectedItem) => selectedItem.id === item.id
			)
			if (index !== -1) {
				baseStore.selectedItems.value.splice(index, 1)
			}
		})
	}

	const selectAllItems = () => {
		baseStore.selectedItems.value = [...baseStore.items.value]
	}

	const clearSelectedItems = () => {
		baseStore.selectedItems.value = []
	}

	// Getters

	const hasCalculationCPTRelatedToSelectedItems = computed(
		() =>
			(giPointItems: GiPointType[] | null = null): boolean => {
				const calculationCPT = useCalculationCPTStore()
				const calculationCPTItems = calculationCPT.getStrippedItems
				const itemsToCheck = giPointItems ?? baseStore.selectedItems.value
				return itemsToCheck.some(
					(selectedItem) =>
						calculationCPTItems.findIndex(
							(item: CalculationCPTType) => item.cpt === selectedItem.properties.cpt.id
						) !== -1
				)
			}
	)

	const getGIPointByCPTId = computed(() => (cptId: number) => {
		return baseStore.items.value.find((item) => item.properties.cpt.id === cptId)
	})

	const getItemById = computed(() => (id: number) => {
		return baseStore.items.value.find((item) => item.id === id)
	})

	const getSelectedItemsByCollectionId = (id: number) => {
		return baseStore.selectedItems.value.filter(
			(item) => item.properties.gi_data_upload_collection === id
		)
	}

	const getItemsByCollectionId = (id: number) => {
		return baseStore.items.value.filter((item) => item.properties.gi_data_upload_collection === id)
	}

	return {
		// State
		...baseStore,
		filterFields,
		// Actions
		resetAll,
		fetchItems,
		removeItem,
		pushSelectedItem,
		replaceAndPushItemsByCollectionId,
		toggleSelectedItem,
		addSelectedItem,
		removeSelectedItem,
		selectAllItemsByCollectionId,
		clearSelectedItemsByCollectionId,
		selectAllItems,
		clearSelectedItems,
		// Getters
		hasCalculationCPTRelatedToSelectedItems,
		getGIPointByCPTId,
		getItemById,
		getSelectedItemsByCollectionId,
		getItemsByCollectionId
	}
})
